/**
 * 批量参考图的**文件名 → 行**归位规则。
 *
 * ===========================================================================
 * 这一层存在的理由（2026-09-29 用户原话）
 * ===========================================================================
 *
 * 「不过 csv 最大的问题是不带图片，真的搞笑」
 * 「而是批量处理这个需要新页面，这样一来，我可以后补图片」
 *
 * 两句说的是同一件事：CSV 是纯文本格式，图片进不去。上一版只认 CSV 里那一列
 * 公网地址，等于要求用户**先把图传图床、再把链接抄回表格** —— 这不是他手上的
 * 工作方式。真实情况永远是：一个文件夹里躺着几十张图，文件名里已经有归属信息
 * （`SKU001_1.jpg`），只是需要有人把它读出来。
 *
 * 所以归位规则不是「顺手加的便利功能」，是这个能力能不能落地的那一环。
 *
 * ===========================================================================
 * 三条设计约束
 * ===========================================================================
 *
 * 1. **纯函数、不碰 IO、不碰上传**。上传那条路（`api.upload` → ImgBB/COS）是
 *    全站唯一的，这里只负责「谁属于哪一行」。分开的好处是：规则可以单测
 *    （`smoke-batch-images`），而上传可以单独验。
 *
 * 2. **认不出就返回 `null`，绝不猜**。归错行的后果比不归严重：图会被塞进别的
 *    产品，出图时「A 的产品配 B 的参考图」，而任务照记 succeeded，
 *    界面上一切正常 —— 这类错误不报错，只能靠人眼在一堆缩略图里发现。
 *    所以宁可列进「未归位」让人点一下，也不要瞎猜一个行号。
 *
 * 3. **匹配规则全部按同一份 `RowKey` 走**，不读 CSV 原文。行在表格里被改过
 *    （改了 sku 或产品名）之后，重新归位用的就是改后的值 —— 否则会出现
 *    「表格里写着 A、归位按旧值走」的两份真源。
 */

/** 归位只需要行的这三个字段 —— 不依赖 `BatchRow` 的其余部分（少耦合一处是一处） */
export type RowKey = {
  /** 文件里的行号（1-based），归位结果带回去的就是它 */
  line: number
  sku: string
  productName: string
}

/**
 * 归位结果：`order` 用于同一行内多张图的排序。
 *
 * ⚠️ `index` 是**原数组下标**，调用方靠它把「上传回来的地址」接回「哪一行」。
 * 为什么不按文件名回查：后端落盘时会把文件名改成 uuid（`storage.save_bytes`），
 * 只在响应的 `fileName` 里回显原名 —— 那是一条**回显**，不是契约。
 * 而 `AssetLibraryContext.upload` 是按 `for (const file of files)` 顺序 push 的，
 * 「第 i 个结果 ↔ 第 i 个输入」是硬保证。前者会随后端换图床实现而悄悄失效，
 * 后者不会。所以归位结果必须带下标出来。
 */
export type Assigned<T> = { index: number; file: T; line: number; order: number; why: string }
export type Unassigned<T> = { index: number; file: T; reason: string }
export type AssignResult<T> = { matched: Assigned<T>[]; unmatched: Unassigned<T>[] }

/** 去掉扩展名。只吃「最后一个点」，`a.b.jpg` → `a.b`（产品名里带点很常见） */
export function fileStem(name: string): string {
  const i = name.lastIndexOf('.')
  // 扩展名长度不超过 5（jpg/jpeg/webp/avif/heic 都在内），超过就当文件名里本来有点
  if (i > 0 && name.length - i - 1 <= 5) return name.slice(0, i)
  return name
}

/** 标识与尾号之间的那个分隔符（`prefixHits` 用） */
const SEP = /^[_\-\s]$/

/**
 * 「整段前缀」命中 —— `A-1_1` 的标识是 `A-1`，不是 `A`。
 *
 * ===========================================================================
 * 为什么必须有这一条（2026-09-29 复盘）
 * ===========================================================================
 *
 * 规则表原来写的是「首段等于 sku（`SKU001_1` → 行 SKU001）」，但实现用的是
 * `segments()`，于是 `A-1_1` 的首段成了 `A`，**含连字符的 SKU 全线匹配不上**，
 * 只能落到最后那条「包含匹配」上。`A-1_1` 确实被兜住了 —— 但那是**运气**：
 *   ① 表里同时有 `A-1` 和 `A-1x` 时，包含匹配「两个都命中」→ 返回 `null`，
 *      用户会莫名其妙地被要求手工指派一张他命名得好好的图；
 *   ② `why` 文案会写「文件名里含 SKU」，而用户做的其实是**前缀**，说法不对。
 * 现在把「前缀」这条**强的**规则做对，包含匹配退回它该待的位置（捞相机名
 * `IMG_2031_SKU001.jpg` 那种）。
 *
 * 判据是「标识后面**紧接一个分隔符**」，这条同时挡住了两个反向误配：
 *   · `A-1_1`   ≠ `A-10`（`a-1_` 与 `a-10` 第 4 位就分岔了）
 *   · `手套盒_1` ≠ 产品名 `手套`（后面是 `盒`，不是分隔符）
 */
function prefixHits(stem: string, rows: RowKey[]): RowKey[] {
  const k = norm(stem)
  return rows.filter((r) =>
    [r.sku, r.productName]
      .map(norm)
      .filter(Boolean)
      .some((id) => k.length > id.length && k.startsWith(id) && SEP.test(k[id.length] ?? '')),
  )
}

/** 尾号：`SKU001_3` → 3；没有尾号 → 0（0 排在 1 之前，正好是「主图在前」） */
function trailingOrder(stem: string): number {
  const m = /[_\-\s]+(\d{1,3})$/.exec(stem)
  if (!m) return 0
  const n = Number(m[1])
  return Number.isFinite(n) ? n : 0
}

const norm = (s: string) => s.trim().toLowerCase()

/**
 * 文件名 → 行号。返回 `null` 表示**认不出**（不是「失败」，是需要人点一下）。
 *
 * 规则按优先级从上到下，**第一条命中的就用**：
 *
 *   1. 整段等于某行的 `sku`
 *   2. 整段等于某行的产品名
 *   3. **整段前缀**等于某行的 `sku` / 产品名，且后面紧跟分隔符
 *      （`A-1_1` → 行 `A-1`；`A-1_1` **不会**命中 `A-10`）—— 见 `prefixHits`
 *   4. 纯数字 → **行序号**（1-based）：没 sku、图叫 `1.jpg` `2.jpg` 的那种
 *   5. 文件名**包含**某行的 `sku`（长度 ≥3），且**唯一命中**
 *      （`IMG_2031_SKU001.jpg` 这类相机命名）
 *
 * 为什么 4 排在 5 之前：纯数字是个**非常明确的信号**（人不会随手把文件叫 `1`），
 * 而「包含」最容易被误伤 —— 短 sku（`A1`）会命中一大片。先信强的信号。
 *
 * ⚠️ 这里**曾经还有一条**「首段等于 sku / 产品名」，2026-09-29 删了：
 * 它被第 3 条**完全包含** —— 既然 `segments()` 是按同一组分隔符切的，
 * 「第一段恰好等于标识」就等价于「`stem` 以标识开头且后面紧跟分隔符」，
 * 也就是第 3 条的判据本身。留着它不会改变任何一次归位结果，
 * 只会让下一个人以为这两条各管一类情形（于是照着它再写第三个变体）。
 */
export function lineForFile(name: string, rows: RowKey[]): { line: number; why: string } | null {
  if (!rows.length) return null
  const stem = fileStem(name)
  const key = norm(stem)
  if (!key) return null

  // 1 / 2：整段相等
  const exact = rows.filter((r) => (r.sku && norm(r.sku) === key) || (r.productName && norm(r.productName) === key))
  if (exact.length === 1) return { line: exact[0].line, why: '文件名就是行标识' }
  if (exact.length > 1) return null // 两行同名 → 不猜

  // 3：整段前缀（标识后面跟分隔符）—— 含连字符的 SKU 靠这条
  const pre = prefixHits(stem, rows)
  if (pre.length === 1) {
    const id = pre[0].sku && key.startsWith(norm(pre[0].sku)) ? pre[0].sku : pre[0].productName
    return { line: pre[0].line, why: `文件名前缀「${id}」对上了` }
  }
  if (pre.length > 1) return null // 两行都能当前缀 → 不猜

  // 4：纯数字 = 行序号（按**表格里的第几行**数，不是 line 字段 —— 用户数的是顺序）
  if (/^\d{1,4}$/.test(key)) {
    const idx = Number(key)
    if (idx >= 1 && idx <= rows.length) return { line: rows[idx - 1].line, why: `文件名是行号 ${idx}` }
    return null
  }

  // 5：包含 sku 且唯一命中。
  //    门槛是 **3 个字符**，不是 2：`A1` 这种两字符的 SKU 在 `IMG_2031_A1.jpg` 里
  //    会命中一大片（而且人眼看上去「挺像」），2 字符的包含匹配本质上是在猜。
  //    宁可落到「未归位」让用户点一下 —— 见文件头约束 2。
  const hits = rows.filter((r) => r.sku && norm(r.sku).length >= 3 && key.includes(norm(r.sku)))
  if (hits.length === 1) return { line: hits[0].line, why: `文件名里含 SKU「${hits[0].sku}」` }

  return null
}

/**
 * 一批文件 → 归位结果。
 *
 * 同一行允许多张（这正是参考图要的样子：每个产品 3-5 张）；`order` 决定它们
 * 在该行里的先后 —— 用户按 `_1 _2 _3` 命名就是在表达顺序，第一张仍是主图。
 */
export function assignImagesToRows<T extends { name: string }>(files: T[], rows: RowKey[]): AssignResult<T> {
  const matched: Assigned<T>[] = []
  const unmatched: Unassigned<T>[] = []
  files.forEach((file, index) => {
    const hit = lineForFile(file.name, rows)
    if (hit) matched.push({ index, file, line: hit.line, order: trailingOrder(fileStem(file.name)), why: hit.why })
    else
      unmatched.push({
        index,
        file,
        reason: rows.length
          ? '文件名里找不到能对上任何一行的标识（SKU 或产品名）'
          : '表格里还没有行，先把 CSV 导进来',
      })
  })
  // 按行分组、组内按尾号 —— 这样「3-5 张一组」的顺序与用户命名一致。
  // 同序同尾号时按下标兜底，保证顺序**稳定**（调用方要按下标接地址）。
  matched.sort((a, b) => (a.line === b.line ? a.order - b.order || a.index - b.index : a.line - b.line))
  return { matched, unmatched }
}

/** 归位结果 → 「行号 → 图地址」的补丁，供调用方一次性并进行里 */
export function groupByLine<T extends { url: string }>(items: Assigned<T>[]): Map<number, string[]> {
  const map = new Map<number, string[]>()
  for (const it of items) {
    const list = map.get(it.line) ?? []
    // 去重：同一个地址重复归位（用户拖了两次同一张）不该在行里出现两次
    if (!list.includes(it.file.url)) list.push(it.file.url)
    map.set(it.line, list)
  }
  return map
}
